import { useEffect, useState } from 'react';
import useAuthContext from '../../hooks/useAuthContext';
import PropTypes from 'prop-types';
import queryRecharge from '../../services/recharge/queryRecharge';
import InfoCheckout from './InfoCheckout';
import { Fade, useToast } from '@chakra-ui/react';
import SelectBeneficiary from './SelectBeneficiary';
import FormCheckout from './FormCheckout';
import ConfirmRecharge from './ConfirmRecharge';
import { useQueryClient } from '@tanstack/react-query';

const Checkout = ({
  recharge: rechargeProp,
  type = 'movil',
  onClose,
  onSuccess,
  prevCheckoutData,
}) => {
  /* Hooks */
  const { isAuthenticated } = useAuthContext();
  const toast = useToast();
  const queryClient = useQueryClient();

  /* States */
  const [checkoutData, setCheckoutData] = useState(prevCheckoutData || null);
  const [recharge, setRecharge] = useState(rechargeProp || null);
  const [proceedToPayment, setProceedToPayment] = useState(!!rechargeProp?.contacts?.length);

  /* RQ - only call recharge-create if no prevCheckoutData with real id */
  const { data: checkoutDataQuery } = queryRecharge().rechargeCreate(
    prevCheckoutData?.id ? null : recharge,
  );

  /* Effects */
  useEffect(() => {
    if (!isAuthenticated) {
      if (!toast.isActive('login-required')) {
        toast({
          id: 'login-required',
          title: 'Inicie sesión',
          description: 'Debe iniciar sesión para recargar',
          status: 'info',
          duration: 3000,
          position: 'top-right',
          isClosable: true,
        });
      }
      queryClient.removeQueries('paymentStripeIntent');
      onClose();
    }
  }, [isAuthenticated, toast, queryClient, onClose]);

  useEffect(() => {
    if (prevCheckoutData?.id) {
      setCheckoutData(prevCheckoutData);
    }
  }, [prevCheckoutData]);

  useEffect(() => {
    if (checkoutDataQuery) {
      setCheckoutData(checkoutDataQuery);
    }
  }, [checkoutDataQuery]);

  useEffect(() => {
    if (recharge?.contacts?.length) {
      sessionStorage.setItem('pending_recharge_contacts', JSON.stringify(recharge.contacts));
    }
  }, [recharge?.contacts]);

  /* Functions */
  const handleError = () => {
    onClose();
    queryClient.removeQueries('paymentStripeIntent');
    toast({
      title: 'Error al recargar',
      description: 'No se pudo realizar la recarga',
      status: 'error',
      duration: 3000,
      position: 'top-right',
      isClosable: true,
    });
  };

  const handleAddRecharge = (additionalData) => {
    setRecharge({ ...recharge, ...additionalData });
    setProceedToPayment(false);
  };

  return (
    <>
      <div className="flex flex-col gap-10 items-center lg:flex-row select-none min-h-[460px] transition-all md:px-10">
        <>
          {/* Recharge Info */}
          <InfoCheckout
            checkoutData={checkoutData}
            contacts={recharge?.contacts || []}
          />

          {/* Contact / Stripe column */}
          <div className="flex flex-col items-center justify-center min-h-[460px]">
            {!recharge?.contacts ? (
              <Fade in={!recharge?.contacts?.length}>
                <SelectBeneficiary
                  setRecharge={handleAddRecharge}
                  type={type}
                />
              </Fade>
            ) : !proceedToPayment ? (
              <Fade in={!!recharge?.contacts?.length}>
                <ConfirmRecharge
                  recharge={recharge}
                  checkoutData={checkoutData}
                  onClose={onClose}
                  onSuccess={onSuccess}
                  onProceedToPayment={() => setProceedToPayment(true)}
                />
              </Fade>
            ) : (
              <Fade in={proceedToPayment}>
                <FormCheckout
                  checkoutData={checkoutData}
                  onSuccess={onSuccess}
                  handleError={handleError}
                />
              </Fade>
            )}
          </div>
        </>
      </div>
    </>
  );
};

Checkout.propTypes = {
  recharge: PropTypes.shape({
    rechargeable_type: PropTypes.string,
    rechargeable_id: PropTypes.number,
    currency: PropTypes.string,
    contacts: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        phone: PropTypes.string,
      }),
    ),
  }),
  type: PropTypes.string,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  prevCheckoutData: PropTypes.shape({
    id: PropTypes.number,
    rechargeable_object: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
    }),
    rechargeable_type: PropTypes.string,
    type: PropTypes.string,
  }),
};

export default Checkout;
